import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Combobox,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircleFilled,
  CopyRegular,
  DismissCircleFilled,
  PersonAddRegular,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import type { CreateUserResult, DirectoryUserDto } from '../api/types';
import { deriveDisplayName, deriveUpnPrefix, pickDefaultDomain } from '../features/users/upn';

const COUNTRIES = [
  { code: 'DE', name: 'Deutschland' },
  { code: 'AT', name: 'Österreich' },
  { code: 'CH', name: 'Schweiz' },
  { code: 'GB', name: 'Vereinigtes Königreich' },
  { code: 'US', name: 'USA' },
  { code: 'FR', name: 'Frankreich' },
  { code: 'PL', name: 'Polen' },
  { code: 'NL', name: 'Niederlande' },
];

const formWidth = { maxWidth: '640px' };

export function CreateUserPage() {
  const api = useApi();

  const domains = useAsync(() => api.getDomains(), [api]);
  const licenses = useAsync(() => api.getLicenses(), [api]);
  const services = useAsync(() => api.getServices(), [api]);
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'users.create')?.ticketMode ?? 'None';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [upnPrefix, setUpnPrefix] = useState('');
  const [upnEdited, setUpnEdited] = useState(false);
  const [domain, setDomain] = useState('');
  const [usageLocation, setUsageLocation] = useState('DE');

  const [managerText, setManagerText] = useState('');
  const [managerResults, setManagerResults] = useState<DirectoryUserDto[]>([]);
  const [selectedManager, setSelectedManager] = useState<DirectoryUserDto | undefined>();
  const managerTimer = useRef<number | undefined>(undefined);

  const [licenseSkuId, setLicenseSkuId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [ticketNumber, setTicketNumber] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateUserResult | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Domain-Standard setzen, sobald geladen
  useEffect(() => {
    if (domains.data && domains.data.length > 0 && !domain) {
      setDomain(pickDefaultDomain(domains.data));
    }
  }, [domains.data, domain]);

  // UPN-Präfix aus Namen ableiten, solange nicht manuell geändert
  useEffect(() => {
    if (!upnEdited) {
      setUpnPrefix(deriveUpnPrefix(firstName, lastName));
    }
  }, [firstName, lastName, upnEdited]);

  const displayName = deriveDisplayName(firstName, lastName);
  const upn = upnPrefix && domain ? `${upnPrefix}@${domain}` : '';

  const selectedLicense = useMemo(
    () => licenses.data?.find((l) => l.skuId === licenseSkuId),
    [licenses.data, licenseSkuId],
  );
  const selectedServiceNames = useMemo(
    () =>
      (services.data ?? [])
        .filter((s) => selectedServiceIds.includes(s.id))
        .map((s) => s.name)
        .join(', '),
    [services.data, selectedServiceIds],
  );

  function onManagerInput(text: string) {
    setManagerText(text);
    setSelectedManager(undefined);
    if (managerTimer.current) window.clearTimeout(managerTimer.current);
    if (text.trim().length < 2) {
      setManagerResults([]);
      return;
    }
    managerTimer.current = window.setTimeout(() => {
      api
        .searchUsers(text.trim())
        .then(setManagerResults)
        .catch(() => setManagerResults([]));
    }, 300);
  }

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = 'Vorname ist erforderlich.';
    if (!lastName.trim()) errs.lastName = 'Nachname ist erforderlich.';
    if (!upnPrefix.trim()) errs.upn = 'Anmeldename ist erforderlich.';
    if (!domain) errs.domain = 'Domain ist erforderlich.';
    if (licenseSkuId && !usageLocation) errs.usageLocation = 'Für eine Lizenz wird der Nutzungsstandort benötigt.';
    if (ticketMode === 'Required' && !ticketNumber.trim()) errs.ticket = 'Ticketnummer ist erforderlich.';
    return errs;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.createUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        userPrincipalName: upn,
        usageLocation,
        managerId: selectedManager?.id,
        licenseSkuId: licenseSkuId || undefined,
        serviceIds: selectedServiceIds,
        ticketNumber: ticketNumber.trim() || undefined,
      });
      if (res.status === 'PendingApproval') {
        setPending(true);
      } else if (res.result) {
        setResult(res.result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function resetForm() {
    setFirstName('');
    setLastName('');
    setUpnPrefix('');
    setUpnEdited(false);
    setUsageLocation('DE');
    setManagerText('');
    setManagerResults([]);
    setSelectedManager(undefined);
    setLicenseSkuId('');
    setSelectedServiceIds([]);
    setTicketNumber('');
    setResult(null);
    setPending(false);
    setError(null);
    setFieldErrors({});
  }

  if (pending) {
    return (
      <div>
        <PageHeader title="Benutzer anlegen" />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
              Diese Aktion ist freigabepflichtig. Der Antrag wartet auf Genehmigung — du findest ihn unter „Meine
              Anfragen". Nach der Freigabe erscheinen dort auch das Ergebnis und das temporäre Passwort.
            </MessageBarBody>
          </MessageBar>
          <div>
            <Button appearance="primary" icon={<PersonAddRegular />} onClick={resetForm}>
              Weiteren Antrag stellen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div>
        <PageHeader title="Benutzer anlegen" />
        <CreatedUserResultView result={result} onReset={resetForm} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Benutzer anlegen"
        description="Joiner-Prozess: neuen Mitarbeiter inkl. Lizenz und App-Zugriff anlegen."
      />

      {error ? (
        <div style={{ marginBottom: tokens.spacingVerticalM, ...formWidth }}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Anlegen fehlgeschlagen</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      ) : null}

      <form onSubmit={onSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, ...formWidth }}>
          {ticketMode !== 'None' ? (
            <Field
              label="Ticketnummer"
              required={ticketMode === 'Required'}
              validationMessage={fieldErrors.ticket}
              hint={ticketMode === 'Required' ? 'Für diese Aktion ist eine Ticketnummer Pflicht.' : 'Optional.'}
            >
              <Input value={ticketNumber} onChange={(_, d) => setTicketNumber(d.value)} placeholder="z. B. INC0012345" />
            </Field>
          ) : null}
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
            <Field label="Vorname" required validationMessage={fieldErrors.firstName} style={{ flex: 1 }}>
              <Input value={firstName} onChange={(_, d) => setFirstName(d.value)} />
            </Field>
            <Field label="Nachname" required validationMessage={fieldErrors.lastName} style={{ flex: 1 }}>
              <Input value={lastName} onChange={(_, d) => setLastName(d.value)} />
            </Field>
          </div>

          <Field
            label="Anmeldename (UPN)"
            required
            validationMessage={fieldErrors.upn ?? fieldErrors.domain}
            hint={upn ? `Anmeldung als: ${upn}` : 'Wird aus Vor- und Nachname gebildet.'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
              <Input
                style={{ flex: 1 }}
                value={upnPrefix}
                onChange={(_, d) => {
                  setUpnEdited(true);
                  setUpnPrefix(d.value);
                }}
              />
              <Text>@</Text>
              <Dropdown
                style={{ minWidth: '220px' }}
                placeholder={domains.loading ? 'Lade…' : 'Domain'}
                value={domain}
                selectedOptions={domain ? [domain] : []}
                onOptionSelect={(_, d) => d.optionValue && setDomain(d.optionValue)}
              >
                {(domains.data ?? []).map((dom) => (
                  <Option key={dom} value={dom} text={dom}>
                    {dom}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </Field>

          <Field label="Manager" hint="Optional — Person suchen und auswählen.">
            <Combobox
              freeform
              placeholder="Name oder E-Mail eingeben…"
              value={managerText}
              selectedOptions={selectedManager ? [selectedManager.id] : []}
              onInput={(ev) => onManagerInput((ev.target as HTMLInputElement).value)}
              onOptionSelect={(_, d) => {
                const u = managerResults.find((r) => r.id === d.optionValue);
                if (u) {
                  setSelectedManager(u);
                  setManagerText(u.displayName);
                }
              }}
            >
              {managerResults.map((u) => (
                <Option key={u.id} value={u.id} text={u.displayName}>
                  <span className="pii">
                    {u.displayName} · {u.userPrincipalName}
                  </span>
                </Option>
              ))}
            </Combobox>
          </Field>

          <Field
            label="M365-Lizenz"
            hint="Optional — verfügbare Lizenzen mit freien/belegten Plätzen."
            validationMessage={fieldErrors.usageLocation}
          >
            <Dropdown
              placeholder={licenses.loading ? 'Lade Lizenzen…' : 'Lizenz wählen (optional)'}
              value={selectedLicense ? selectedLicense.displayName : ''}
              selectedOptions={licenseSkuId ? [licenseSkuId] : []}
              onOptionSelect={(_, d) => setLicenseSkuId(d.optionValue ?? '')}
            >
              <Option value="" text="">
                Keine Lizenz
              </Option>
              {(licenses.data ?? []).map((l) => (
                <Option key={l.skuId} value={l.skuId} text={l.displayName} disabled={l.available <= 0}>
                  {l.displayName} — {l.available} frei / {l.consumed} belegt
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Nutzungsstandort" hint="Pflicht, sobald eine Lizenz zugewiesen wird.">
            <Dropdown
              value={COUNTRIES.find((c) => c.code === usageLocation)?.name ?? usageLocation}
              selectedOptions={[usageLocation]}
              onOptionSelect={(_, d) => d.optionValue && setUsageLocation(d.optionValue)}
            >
              {COUNTRIES.map((c) => (
                <Option key={c.code} value={c.code} text={c.name}>
                  {c.name} ({c.code})
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Zugriff auf Enterprise Apps / Services" hint="Über die hinterlegten Gruppen wird der Zugriff erteilt.">
            <Dropdown
              multiselect
              placeholder={services.loading ? 'Lade Services…' : 'Services wählen'}
              value={selectedServiceNames}
              selectedOptions={selectedServiceIds}
              onOptionSelect={(_, d) => setSelectedServiceIds(d.selectedOptions)}
            >
              {(services.data ?? [])
                .filter((s) => s.enabled)
                .map((s) => (
                  <Option key={s.id} value={s.id} text={s.name}>
                    {s.name}
                  </Option>
                ))}
            </Dropdown>
          </Field>

          {displayName ? (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Anzeigename: <strong>{displayName}</strong>
            </Caption1>
          ) : null}

          <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalS }}>
            <Button appearance="primary" type="submit" icon={<PersonAddRegular />} disabled={submitting}>
              {submitting ? 'Lege an…' : 'Benutzer anlegen'}
            </Button>
            {submitting ? <Spinner size="tiny" /> : null}
          </div>
        </div>
      </form>
    </div>
  );
}

function CreatedUserResultView({ result, onReset }: { result: CreateUserResult; onReset: () => void }) {
  const [copied, setCopied] = useState(false);

  function copyPassword() {
    navigator.clipboard
      .writeText(result.temporaryPassword)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, ...formWidth }}>
      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>Benutzer angelegt</MessageBarTitle>
          {result.displayName} ({result.userPrincipalName})
        </MessageBarBody>
      </MessageBar>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, padding: tokens.spacingVerticalL }}>
        <div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Temporäres Passwort</Caption1>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <Text font="monospace" size={400}>
              {result.temporaryPassword}
            </Text>
            <Button
              size="small"
              appearance="subtle"
              icon={<CopyRegular />}
              onClick={copyPassword}
            >
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
          </div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Muss bei der ersten Anmeldung geändert werden.
          </Caption1>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Ausgeführte Schritte</Caption1>
          {result.steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              {step.success ? (
                <CheckmarkCircleFilled style={{ color: tokens.colorPaletteGreenForeground1 }} />
              ) : (
                <DismissCircleFilled style={{ color: tokens.colorPaletteRedForeground1 }} />
              )}
              <Body1>{step.step}</Body1>
              {step.message ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>— {step.message}</Caption1> : null}
            </div>
          ))}
        </div>
      </Card>

      <div>
        <Button appearance="primary" icon={<PersonAddRegular />} onClick={onReset}>
          Weiteren Benutzer anlegen
        </Button>
      </div>
    </div>
  );
}
